import React from 'react';

import { type SsoProvider } from '../../config';
import { ProviderIcon } from '../ProviderIcon';

import { IconWrap, Label, Root, Spinner } from './ProviderButton.styles';

export type ProviderButtonState = 'idle' | 'loading' | 'disabled';

/** Copy context: 'signin' on /login ("Sign in with X"), 'signup' on /register ("Sign up with X"). */
export type ProviderButtonMode = 'signin' | 'signup';

export type ProviderButtonProps = {
  provider: SsoProvider;
  state?: ProviderButtonState;
  /** Drives the label verb; defaults to 'signin'. */
  mode?: ProviderButtonMode;
  onClick: () => void;
};

/**
 * One provider entry in the SSO bar: a wide, labeled pill (brand glyph + "Sign in / Sign up with X",
 * per `mode`). The
 * visible text IS the accessible name, so no separate `aria-label` is needed; `aria-busy` still marks
 * the loading state. Presentational only, no auth logic (that lives in the per-provider hooks/thunks).
 * The click is SELF-GATED: `loading`/`disabled` suppress the handler and set the native `disabled`
 * attribute.
 */
export const ProviderButton = ({
  provider,
  state = 'idle',
  mode = 'signin',
  onClick,
}: ProviderButtonProps): React.ReactElement => {
  const inactive = state !== 'idle';

  const handleClick = (): void => {
    if (inactive) {
      return;
    }
    onClick();
  };

  // Four LITERAL branches (verb x provider) so i18next-cli extracts every copy string.
  const label =
    mode === 'signup' ? (
      provider === 'google' ? (
        <Label text="Sign up with Google" />
      ) : (
        <Label text="Sign up with Telegram" />
      )
    ) : provider === 'google' ? (
      <Label text="Sign in with Google" />
    ) : (
      <Label text="Sign in with Telegram" />
    );

  return (
    <Root
      type="button"
      $loading={state === 'loading'}
      disabled={inactive}
      aria-busy={state === 'loading'}
      onClick={handleClick}
      data-provider={provider}
    >
      <IconWrap aria-hidden="true">
        {state === 'loading' ? <Spinner /> : <ProviderIcon provider={provider} />}
      </IconWrap>
      {label}
    </Root>
  );
};

export default ProviderButton;
